import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'

export type TicketIssue = Readonly<{
  ticketId: string
  eventLocator: string
  publicToken: string
  reference: string
  issuedNow: boolean
}>

export type PublicTicket = Readonly<{
  attendeeName: string
  eventName: string
  eventDate: string
  endDate: string | null
  startTime: string | null
  endTime: string | null
  timezone: string
  location: string
  reference: string
  /** The display name of the team member who registered the attendee, when they've set one. */
  invitedBy: string | null
}>

export type TicketServiceErrorCode = 'offline' | 'ineligible' | 'not-found' | 'unexpected'

export class TicketServiceError extends Error {
  constructor(public readonly code: TicketServiceErrorCode, message: string) {
    super(message)
    this.name = 'TicketServiceError'
  }
}

type TicketIssueRow = {
  ticket_id: string
  event_locator: string
  public_token: string
  reference: string
  issued_now: boolean
}

type PublicTicketRow = {
  attendee_name: string | null
  event_name: string
  event_date: string
  end_date: string | null
  start_time: string | null
  end_time: string | null
  timezone: string
  location: string
  reference: string
  invited_by?: string | null
}

function requireOnline(message: string) {
  if (!navigator.onLine) throw new TicketServiceError('offline', message)
}

function mapIssue(row: TicketIssueRow): TicketIssue {
  return {
    ticketId: row.ticket_id,
    eventLocator: row.event_locator,
    publicToken: row.public_token,
    reference: row.reference,
    issuedNow: row.issued_now,
  }
}

function mapPublicTicket(row: PublicTicketRow): PublicTicket | null {
  // The database only returns a row for a valid issued ticket. This guard also
  // keeps a malformed backend result from turning into a partially public UI.
  if (!row.attendee_name) return null
  return {
    attendeeName: row.attendee_name,
    eventName: row.event_name,
    eventDate: row.event_date,
    endDate: row.end_date,
    startTime: row.start_time,
    endTime: row.end_time,
    timezone: row.timezone,
    location: row.location,
    reference: row.reference,
    invitedBy: row.invited_by?.trim() || null,
  }
}

/** Builds the sole ticket URL shape; callers must never derive paths ad hoc. */
export function publicTicketPath(eventLocator: string, publicToken: string): string {
  // Short secrets (22 characters) live under /t/; long ones, in links handed
  // out before short links, under /ticket/.
  const prefix = /^[0-9a-f]{64}$/.test(publicToken) ? 'ticket' : 't'
  return `/${prefix}/${encodeURIComponent(eventLocator)}/${encodeURIComponent(publicToken)}`
}

/**
 * The bearer token stays in the returned URL only. Callers must not log it,
 * persist it in IndexedDB, or put it in analytics/error-reporting metadata.
 */
export function publicTicketUrl(issue: TicketIssue, origin = window.location.origin): string {
  return new URL(publicTicketPath(issue.eventLocator, issue.publicToken), origin).toString()
}

export interface TicketService {
  issue(registrationId: string): Promise<TicketIssue>
  resolvePublic(eventLocator: string, publicToken: string): Promise<PublicTicket | null>
}

export function createTicketService(client: SupabaseClient = supabase): TicketService {
  return {
    async issue(registrationId) {
      requireOnline('Reconnect to issue this ticket.')
      const result = await client.rpc('issue_ticket', { ticket_registration_id: registrationId }) as unknown as { data: TicketIssueRow[] | null; error: { code?: string; message?: string } | null }
      if (result.error?.code === '22023') throw new TicketServiceError('ineligible', 'Tickets are available only for ticketed events.')
      if (result.error?.code === 'P0002') throw new TicketServiceError('not-found', 'This registration is no longer available.')
      if (result.error) throw new TicketServiceError('unexpected', 'The ticket could not be issued. Please try again.')
      const row = result.data?.[0]
      if (!row) throw new TicketServiceError('unexpected', 'The ticket could not be issued. Please try again.')
      return mapIssue(row)
    },
    async resolvePublic(eventLocator, publicToken) {
      requireOnline('Reconnect to view this ticket.')
      const result = await client.rpc('resolve_public_ticket', { event_locator: eventLocator, ticket_token: publicToken }) as unknown as { data: PublicTicketRow[] | null; error: unknown }
      if (result.error) throw new TicketServiceError('unexpected', 'This ticket could not be loaded. Please try again.')
      return result.data?.[0] ? mapPublicTicket(result.data[0]) : null
    },
  }
}

export const ticketService = createTicketService()
